import { useState } from "react";
import Header from "@/pages/components/Header";
import Footer from "@/pages/components/Footer";
import WorkCard from "@/pages/components/WorkCard";
import { categories, works } from "@/mocks/manhwaData";

type SortMode = "popularity" | "rating" | "newest";

export default function Rankings() {
  const [mode, setMode] = useState<SortMode>("popularity");
  const [catId, setCatId] = useState<string>("all");

  const filtered = catId === "all" ? works : works.filter((w) => w.categoryId === catId);
  const sorted = [...filtered].sort((a, b) => {
    if (mode === "popularity") return b.popularity - a.popularity;
    if (mode === "rating") return b.rating - a.rating;
    return a.status === "连载中" ? -1 : 1;
  });

  const rankList = sorted.slice(0, 10);

  return (
    <>
      <Header />

      <main className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <nav aria-label="面包屑导航" className="mb-6">
          <ol className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <li>
              <a href="/" className="transition-colors hover:text-primary-600">
                首页
              </a>
            </li>
            <li>
              <i className="ri-arrow-right-s-line"></i>
            </li>
            <li aria-current="page" className="text-foreground-800">
              人气榜单
            </li>
          </ol>
        </nav>

        <header className="mb-8">
          <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">韩漫人气榜单</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            综合人气值、评分与更新状态实时排序的人气榜单，从甜宠到悬疑、从奇幻到现实，轻松查看当前最热门的韩漫作品。
          </p>
        </header>

        {/* Filters */}
        <div className="flex flex-col gap-4 rounded-2xl border border-background-200/70 bg-background-100 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-sm text-foreground-600">排序方式</span>
            <div className="flex gap-1 rounded-full bg-background-50 p-1">
              {(
                [
                  { key: "popularity", label: "人气值" },
                  { key: "rating", label: "评分" },
                  { key: "newest", label: "连载中" },
                ] as { key: SortMode; label: string }[]
              ).map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setMode(opt.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    mode === opt.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCatId("all")}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                catId === "all"
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-50 text-foreground-600 hover:text-accent-700"
              }`}
            >
              全部分类
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setCatId(cat.id)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                  catId === cat.id
                    ? "bg-accent-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:text-accent-700"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Ranking list */}
        <div className="mt-8 space-y-3">
          {rankList.map((work, i) => (
            <a
              key={work.id}
              href={`/categories#${work.categoryId}`}
              className="flex items-center gap-4 rounded-2xl border border-background-200/70 bg-background-100 p-3.5 transition-colors hover:bg-background-50"
            >
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl font-heading text-lg ${
                  i === 0
                    ? "bg-primary-500 text-background-50"
                    : i === 1
                    ? "bg-secondary-500 text-background-50"
                    : i === 2
                    ? "bg-accent-500 text-background-50"
                    : "bg-secondary-100 text-secondary-900"
                }`}
              >
                {i + 1}
              </span>
              <div className="relative h-24 w-16 shrink-0 overflow-hidden rounded-lg">
                <img
                  src={work.cover}
                  alt={`${work.title}韩漫榜单封面`}
                  title={`${work.title} · 韩漫人气榜单`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-sm font-semibold text-foreground-950 md:text-base">
                  {work.title}
                </h2>
                <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
                  <span>{work.categoryName}</span>
                  <span className={`font-medium ${work.status === "连载中" ? "text-accent-600" : "text-secondary-700"}`}>
                    {work.status}
                  </span>
                  <span>{work.chapters}话</span>
                </p>
                <p className="mt-1.5 hidden truncate text-xs text-foreground-500 sm:block">
                  {work.summary}
                </p>
              </div>
              <div className="hidden text-right sm:block">
                <p className="flex items-center justify-end gap-1 text-lg font-semibold text-primary-600">
                  {work.popularity}
                  <span className="text-xs font-normal text-foreground-500">人气</span>
                </p>
                <p className="mt-0.5 flex items-center justify-end gap-0.5 text-sm text-secondary-600">
                  <i className="ri-star-fill"></i>
                  {work.rating.toFixed(1)}
                </p>
              </div>
            </a>
          ))}
        </div>

        {/* Grid of all works */}
        <section className="mt-16">
          <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">
            {mode === "popularity" && "全站人气作品一览"}
            {mode === "rating" && "高分口碑作品一览"}
            {mode === "newest" && "正在连载的作品"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">
            以下作品同样值得关注，欢迎前往分类页探索更多正版韩漫题材。
          </p>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {sorted.slice(0, 8).map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}